import type { TreeNodeType } from '../../lib/tree'
import type {
	DataChoice,
	FieldConfigType,
	FieldDefinition,
	FilterRuleEntity,
	Localization,
} from './types'

import React from 'react'
import uniq from 'lodash/fp/uniq'
import {
	AlignJustifyIcon,
	CalendarIcon,
	ClockIcon,
	HashIcon,
	ListTreeIcon,
	PercentIcon,
	ShapesIcon,
	ToggleLeftIcon,
	TypeIcon,
} from 'lucide-react'

import {
	operationsConfigs,
	Operators,
	ValueEditorType,
	ValueType,
} from './constants'
import {
	BooleanFilter,
	DateFilter,
	DateRangeFilter,
	DatetimeRangeFilter,
	MultiSelectFilter,
	NumberFilter,
	PercentFilter,
	SelectFilter,
	StringFilter,
	TreeSelectFilter,
} from './fields'
import { localization } from './localization'
import { isReferenceRule } from './utils/utils'

/**
 * Get editor depend on given editor type
 * @param type ValueEditorType - editor type
 * @returns - editor component
 */
export function getFieldByEditorType(type?: ValueEditorType) {
	switch (type) {
		case ValueEditorType.text:
			return StringFilter
		case ValueEditorType.number:
			return NumberFilter
		case ValueEditorType.daterange:
			return DateRangeFilter
		case ValueEditorType.datetimerange:
			return DatetimeRangeFilter
		case ValueEditorType.date:
			return DateFilter
		case ValueEditorType.select:
			return SelectFilter
		case ValueEditorType.multiselect:
			return MultiSelectFilter
		case ValueEditorType.checkbox:
			return BooleanFilter
		case ValueEditorType.checkboxTree:
			return TreeSelectFilter
		case ValueEditorType.percent:
			return PercentFilter
		default:
			return null
	}
}
/**
 * Get editor depend on given value type
 * @param type ValueEditorType - value type
 * @returns - editor component
 */
export function getFieldByValueType(type: ValueType) {
	switch (type) {
		case ValueType.float:
		case ValueType.integer:
		case ValueType.number:
			return NumberFilter
		case ValueType.boolean:
			return BooleanFilter
		case ValueType.string:
			return StringFilter
		case ValueType.array:
			return MultiSelectFilter
		case ValueType.datetime:
			return DatetimeRangeFilter
		case ValueType.date:
			return DateFilter
		default:
			return StringFilter
	}
}

/**
 * Get default editor type depend on data type
 * @example getDefaultEditorByValueType(ValueType.string) // ValueEditorType.text
 * @param type - data type
 * @returns - default editor type
 */
export function getDefaultEditorByValueType(type: ValueType) {
	switch (type) {
		case ValueType.string:
			return ValueEditorType.text
		case ValueType.number:
		case ValueType.integer:
		case ValueType.float:
			return ValueEditorType.number
		case ValueType.array:
			return ValueEditorType.multiselect
		case ValueType.datetime:
			return ValueEditorType.date
		case ValueType.boolean:
			return ValueEditorType.checkbox
		default:
			return ValueEditorType.text
	}
}
/**
 * Allows to get field icon depend on Editor type
 * @example getEditorIcon(ValueEditorType.checkboxTree) // <RuleIcon />
 * @param type - editor type
 * @returns - icon component
 */
export function getEditorIcon(type: ValueEditorType) {
	switch (type) {
		case ValueEditorType.checkboxTree:
			return <ListTreeIcon className='size-4 opacity-50' />
		case ValueEditorType.date:
			return <CalendarIcon className='size-4 opacity-50' />
		case ValueEditorType.text:
			return <TypeIcon className='size-4 opacity-50' />
		case ValueEditorType.number:
			return <HashIcon className='size-4 opacity-50' />
		case ValueEditorType.select:
		case ValueEditorType.multiselect:
			return <AlignJustifyIcon className='size-4 opacity-50' />
		case ValueEditorType.checkbox:
			return <ToggleLeftIcon className='size-4 opacity-50' />
		case ValueEditorType.daterange:
			return <CalendarIcon className='size-4 opacity-50' />
		case ValueEditorType.datetimerange:
			return <ClockIcon className='size-4 opacity-50' />
		case ValueEditorType.percent:
			return <PercentIcon className='size-4 opacity-50' />
		default:
			return <ShapesIcon className='size-4 opacity-50' />
	}
}

/**
 * Allows to get field icon depend on Editor type
 * @example getEditorIcon(ValueEditorType.checkboxTree) // <RuleIcon />
 * @param type - editor type
 * @returns - icon component
 */
export function getFieldIcon({ name }: { name: string }) {
	switch (name) {
		case 'TreeSelect':
			return <ListTreeIcon className='size-4 shrink-0 opacity-50' />
		case 'Date':
			return <CalendarIcon className='size-4 shrink-0 opacity-50' />
		case 'String':
			return <TypeIcon className='size-4 shrink-0 opacity-50' />
		case 'Number':
			return <HashIcon className='size-4 shrink-0 opacity-50' />
		case 'Select':
		case 'MultiSelect':
			return <AlignJustifyIcon className='size-4 shrink-0 opacity-50' />
		case 'Boolean':
			return <ToggleLeftIcon className='size-4 shrink-0 opacity-50' />
		case 'DateRange':
			return <CalendarIcon className='size-4 shrink-0 opacity-50' />
		case 'DatetimeRange':
			return <ClockIcon className='size-4 shrink-0 opacity-50' />
		case 'Percent':
			return <PercentIcon className='size-4 shrink-0 opacity-50' />
		default:
			return <ShapesIcon className='size-4 shrink-0 opacity-50' />
	}
}

/**
 * Get operations configs for given operations list
 * @param operations - list of operations
 * @returns - operations configs
 * @example getOperationsConfigs([Operators.IN, Operators.EQ]) // { getFilterTypes: () => [], getInitialValue: () => {} }
 */
export const getOperationsConfigs = (operations: Operators[]) => {
	const configs = uniq(operations).map(
		(operation) => operationsConfigs[operation]
	)

	return {
		getFilterTypes() {
			return configs.map(({ operation, label, hasInput }) => ({
				operation,
				label,
				hasInput,
			}))
		},
		getInitialValue() {
			return { operation: configs[0].operation, value: configs[0].defaultValue }
		},
	}
}

/**
 *  Get field config for given fields and fields options
 * @param fields - list of fields definition
 * @param fiedsOptions - fields options
 * @returns - fields config
 * @example
 * ```
 * getFiledConfig(
 * 	[
 * 		{
 * 			label: 'Name',
 * 			value: 'name',
 * 			valueType: ValueType.str,
 * 			valueEditorType: ValueEditorType.text
 * 		}
 * 	],
 * 	{ name: ['John', 'Doe'] }
 * )
 * // returns
 * {
 * 		name: {
 * 			label: 'Name',
 * 			type: StringFilter,
 * 			useSelectOptions: () => ({ data: ['John', 'Doe'] })
 * 		}
 * }
 * ```
 */
export const getFiledConfig = (
	fields: FieldDefinition[],
	fiedsOptions: Record<string, any>
): FieldConfigType =>
	fields.reduce((acc, field) => {
		const type =
			getFieldByEditorType(field.valueEditorType) ||
			getFieldByValueType(field.valueType)
		if (!type) {
			return acc
		}

		const config = { label: field.label, type, valueType: field.valueType }
		if (field.operations && field.operations.length > 0) {
			Object.assign(config, getOperationsConfigs(field.operations))
		}

		if (
			field.valueType === ValueType.string &&
			field.valueEditorType &&
			[ValueEditorType.multiselect, ValueEditorType.checkboxTree].includes(
				field.valueEditorType
			)
		) {
			Object.assign(
				config,
				getOperationsConfigs([Operators.IN, ...(field.operations || [])])
			)
		}
		if (field.valueType === ValueType.array && field.valueEditorType) {
			if (ValueEditorType.multiselect === field.valueEditorType) {
				Object.assign(
					config,
					getOperationsConfigs([
						Operators.ANY_OF,
						Operators.ALL_OF,
						Operators.EXACT,
						Operators.NONE_OF,
						Operators.IS_EMPTY,
						Operators.IS_NOT_EMPTY,
						...(field.operations || []),
					])
				)
			} else if (ValueEditorType.checkboxTree === field.valueEditorType) {
				Object.assign(
					config,
					getOperationsConfigs(field.operations || [Operators.ANY_OF])
				)
			}
		}
		if (fiedsOptions?.[field.value]) {
			Object.assign(config, {
				useSelectOptions: () => ({ data: fiedsOptions?.[field.value] }),
			})
		}
		if (field.enum && field.enum.length > 0) {
			Object.assign(config, {
				useSelectOptions: () => ({
					data: field.enum?.map((value) => ({ label: value, value })),
				}),
			})
		}

		return { ...acc, [field.value]: config }
	}, {})

type RuleDescription = { label: string; operation: string; value: string }
/**
 * Generate query description for given filter rules and fields options
 * @param rules  - list of filter rules
 * @param param1 - options for query description generation
 * @returns - query description in string or json format
 */
export function getQueryDescription(
	rules: FilterRuleEntity[],
	{
		fields = [],
		dataChoices = {},
		locale = localization,
	}: {
		fields: FieldDefinition[]
		dataChoices?: Record<string, DataChoice[] | TreeNodeType[]>
		locale?: Localization
	}
): RuleDescription[] {
	const fieldsConfig = getFiledConfig(fields, dataChoices)
	const getField = (key: string) => {
		const field = fieldsConfig[key]
		if (!field) {
			return undefined
		}
		const fieldModel = new field.type.controller({ key, ...field })

		return fieldModel
	}

	return rules
		.map((rule) => {
			const field = getField(rule.name)
			if (!field) return ''
			const isRef = isReferenceRule(rule)
			const reference = isRef ? getField(rule.reference) : undefined
			const options = dataChoices?.[rule.name] || []
			const formatted = field.formatState?.(rule, options)
			const operation = formatted?.operation
				? locale.getOperatorLabel(formatted.operation)
				: ''
			if (isRef) {
				return {
					label: field.label,
					operation,
					value: reference?.label || rule.reference,
				}
			} else {
				return {
					label: field.label,
					operation,
					value: formatted?.value,
				}
			}
		})
		.filter(Boolean) as RuleDescription[]
}
/**
 * Sanitize filter rule to ensure that we use only one of reference or value
 * @param rule - filter rule
 * @returns - sanitized filter rule
 */
export const sanitizeFilterRule = (
	rule: FilterRuleEntity
): FilterRuleEntity => {
	if (Object.hasOwn(rule, 'reference')) {
		return {
			name: rule.name,
			operation: rule.operation,
			reference: rule.reference,
		}
	} else {
		return {
			name: rule.name,
			operation: rule.operation,
			value: rule.value,
		}
	}
}

export const expectedStringValue = (operation: Operators) =>
	[
		Operators.CONTAINS,
		Operators.NOT_CONTAINS,
		Operators.IS,
		Operators.IS_NOT,
	].includes(operation)
export const expectedArrayValue = (
	state: any
): state is {
	operation:
		| Operators.IN
		| Operators.NOT_IN
		| Operators.EXACT
		| Operators.ANY_OF
		| Operators.NONE_OF
		| Operators.ALL_OF
		| Operators.DESCENDANT_OF
	value: string[]
} =>
	[
		Operators.IN,
		Operators.NOT_IN,
		Operators.ANY_OF,
		Operators.EXACT,
		Operators.NONE_OF,
		Operators.ALL_OF,
		Operators.DESCENDANT_OF,
	].includes(state.operation)
export const expectedNumberRangeValue = (
	state: any
): state is {
	operation: Operators.BETWEEN
	value: [number, number]
} => Operators.BETWEEN === state.operation
export const expectedDateValue = (
	state: any
): state is {
	operation: Operators.DATE_AT | Operators.DATE_BEFORE | Operators.DATE_AFTER
	value: string
} =>
	[Operators.DATE_AT, Operators.DATE_BEFORE, Operators.DATE_AFTER].includes(
		state.operation
	)
export const expectedDateRangeValue = (
	state: any
): state is {
	operation: Operators.DATE_BETWEEN
	value: [string, string]
} => Operators.DATE_BETWEEN === state.operation
export const expectedNumericValue = (operation: Operators) =>
	[
		Operators.GT,
		Operators.LT,
		Operators.EQ,
		Operators.LTE,
		Operators.GTE,
		Operators.NE,
	].includes(operation)
export const expectedBooleanValue = (operation: Operators) =>
	[Operators.IS_EMPTY, Operators.IS_NOT_EMPTY].includes(operation)

export function getCompatableValueType(operation: Operators): ValueType[] {
	switch (operation) {
		case Operators.DATE_BETWEEN:
		case Operators.DATE_AFTER:
		case Operators.DATE_BEFORE:
		case Operators.DATE_AT:
			return [ValueType.date]
		case Operators.ANY_OF:
		case Operators.ALL_OF:
		case Operators.EXACT:
		case Operators.NONE_OF:
			return [ValueType.array]
		case Operators.GT:
		case Operators.LT:
		case Operators.EQ:
		case Operators.LTE:
		case Operators.GTE:
		case Operators.NE:
		case Operators.BETWEEN:
			return [ValueType.number, ValueType.integer]
		case Operators.IS:
		case Operators.IS_NOT:
		case Operators.CONTAINS:
		case Operators.NOT_CONTAINS:
			return [ValueType.string]
		case Operators.IS_EMPTY:
		case Operators.IS_NOT_EMPTY:
			return [ValueType.boolean]
		case Operators.IN:
		case Operators.NOT_IN:
			return [ValueType.array]
		case Operators.BL:
			return [ValueType.boolean]
		default:
			return [ValueType.string]
	}
}

export function adjustValueType(operation: Operators): ValueType {
	switch (operation) {
		case Operators.DATE_BETWEEN:
		case Operators.DATE_AFTER:
		case Operators.DATE_BEFORE:
		case Operators.DATE_AT:
			return ValueType.date
		case Operators.ANY_OF:
		case Operators.ALL_OF:
		case Operators.EXACT:
		case Operators.NONE_OF:
			return ValueType.array
		case Operators.GT:
		case Operators.LT:
		case Operators.EQ:
		case Operators.LTE:
		case Operators.GTE:
		case Operators.NE:
		case Operators.BETWEEN:
			return ValueType.number
		case Operators.BL:
			return ValueType.boolean
		default:
			return ValueType.string
	}
}
